/**
 * ModelRuntimePoliciesPage — /agents/libraries/model-runtime-policies.
 * The runtime policies as a ListPage + DataTable, with the "Kinds of work" rules under the list. A policy opens on its
 * own page (runtime/PolicyDetailPage), a new one on /new (runtime/NewPolicyPage).
 * Demo example: /tables/data-tables (search, pills, select filter, row click); /ai for the policy surface.
 */
import { AlertTriangle, Cpu, Plus, Target } from 'lucide-react';
import type React from 'react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Can } from '../../components/auth/Can.js';
import { ListPage } from '../../components/page/ListPage.js';
import { buttonStyles } from '../../components/tailgrids/core/button.js';
import { Button, Callout, EmptyState, FilterPills, SearchBar, Sheet, Toolbar } from '../../components/ui/index.js';
import { runtimePoliciesQuery } from '../../services/api/runtime-policy.queries.js';
import { PolicyCatalogList } from './runtime/PolicyCatalogList.js';
import { POLICIES_PATH } from './runtime/policy-api.js';
import { ResolverPreviewSection } from './runtime/ResolverPreviewSection.js';
import { FilterSelect } from '../../components/ui/FilterSelect.js';
import { SurfaceRulesSection } from './runtime/SurfaceRulesSection.js';
import { PURPOSES, enumOptions, humanizeEnum } from './runtime/types.js';
import type { ModelRuntimePolicy } from './runtime/types.js';

const NEW_POLICY_PATH = `${POLICIES_PATH}/new`;
const ANY_PURPOSE = '';
const PURPOSE_OPTIONS = enumOptions(PURPOSES);

type PillId = 'all' | 'org' | 'system' | 'active' | 'high-risk';

function matchesPill(policy: ModelRuntimePolicy, pill: PillId): boolean {
  if (pill === 'org') return policy.scope === 'org';
  if (pill === 'system') return policy.scope === 'system';
  if (pill === 'active') return policy.status === 'active';
  if (pill === 'high-risk') return policy.riskTier === 'high' || policy.riskTier === 'privileged';
  return true;
}

const PILLS: { id: PillId; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'org', label: 'Ours' },
  { id: 'system', label: 'System' },
  { id: 'active', label: 'Active' },
  { id: 'high-risk', label: 'High risk' },
];

export const ModelRuntimePoliciesPage: React.FC = () => {
  const [search, setSearch] = useState('');
  const [pill, setPill] = useState<PillId>('all');
  const [purpose, setPurpose] = useState(ANY_PURPOSE);
  const [resolverOpen, setResolverOpen] = useState(false);

  const policiesQuery = useQuery(runtimePoliciesQuery());

  const allPolicies = [...(policiesQuery.data ?? [])].sort((a, b) => {
    if (a.scope !== b.scope) return a.scope === 'org' ? -1 : 1;
    return a.slug.localeCompare(b.slug);
  });

  const query = search.trim().toLowerCase();
  const filtered = allPolicies.filter(
    (policy) =>
      matchesPill(policy, pill) &&
      (purpose === ANY_PURPOSE || policy.purpose === purpose) &&
      (!query || policy.name.toLowerCase().includes(query) || policy.slug.toLowerCase().includes(query)),
  );

  const newPolicyLink = (
    <Can policy="CanManageSystem">
      <Link to={NEW_POLICY_PATH} className={buttonStyles({ variant: 'primary', appearance: 'fill', size: 'sm' })}>
        <Plus size={14} aria-hidden="true" />
        New policy
      </Link>
    </Can>
  );

  const hasFilters = pill !== 'all' || purpose !== ANY_PURPOSE || query !== '';

  return (
    <>
      <ListPage
        title="Runtime policies"
        subtitle="A runtime policy is an ordered list of models plus limits. People pick a policy, never a model; each kind of work has allowed policies and a default."
        breadcrumbs={[{ label: 'Libraries', to: '/agents/libraries' }]}
        actions={
          <>
            <Button size="sm" variant="secondary" onClick={() => setResolverOpen(true)}>
              <Target size={14} aria-hidden="true" />
              Test resolver
            </Button>
            {newPolicyLink}
          </>
        }
        alert={
          policiesQuery.isError ? (
            <Callout
              tone="danger"
              icon={AlertTriangle}
              title="Could not load runtime policies"
              actions={
                <Button size="sm" variant="secondary" onClick={() => policiesQuery.refetch()}>
                  Retry
                </Button>
              }
            >
              {policiesQuery.error instanceof Error ? policiesQuery.error.message : 'An unexpected error occurred.'}
            </Callout>
          ) : undefined
        }
        toolbar={
          <Toolbar
            search={<SearchBar value={search} onChange={setSearch} placeholder="Search policy name or slug…" />}
            pills={
              <FilterPills
                aria-label="Policy scope and status"
                options={PILLS.map((option) => ({
                  ...option,
                  count: allPolicies.filter((policy) => matchesPill(policy, option.id)).length,
                }))}
                value={pill}
                onChange={(id) => setPill(id as PillId)}
              />
            }
            right={
              <FilterSelect
                label="Purpose"
                anyLabel="Any purpose"
                value={purpose}
                options={PURPOSE_OPTIONS}
                onChange={setPurpose}
              />
            }
          />
        }
        footer={<SurfaceRulesSection />}
      >
        {!policiesQuery.isError && (
          <PolicyCatalogList
            policies={filtered}
            loading={policiesQuery.isLoading}
            footer={
              filtered.length > 0 ? (
                <span>
                  Showing {filtered.length} of {allPolicies.length}
                  {purpose !== ANY_PURPOSE ? ` · ${humanizeEnum(purpose)}` : ''}
                </span>
              ) : undefined
            }
            empty={
              hasFilters && allPolicies.length > 0 ? (
                <EmptyState
                  icon={Cpu}
                  title="No policies match"
                  description="Change the search or the filters to see more policies."
                />
              ) : (
                <EmptyState
                  icon={Cpu}
                  title="No runtime policies"
                  description="Create policies to bound how models execute. The resolver needs at least one policy per purpose to route requests."
                  action={newPolicyLink}
                />
              )
            }
          />
        )}
      </ListPage>

      <Sheet
        open={resolverOpen}
        onClose={() => setResolverOpen(false)}
        title="Test resolver"
        description="See which policy a piece of work would run under."
        width="lg"
      >
        <ResolverPreviewSection />
      </Sheet>
    </>
  );
};
